import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { API, getStylePrefs, saveStylePrefs } from "./shared";

const TABS = ["Profile", "Creative Preferences", "Notifications", "Subscription", "Security"];

export default function SettingsPage() {
  const { customer, briefs, refresh } = useOutletContext();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Profile");
  const [prefs, setPrefs] = useState(() => getStylePrefs());
  const [newPref, setNewPref] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelState, setCancelState] = useState("idle");

  const renewalDate = new Date(customer.billing_reset_date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });

  const addPref = (e) => {
    e.preventDefault();
    const v = newPref.trim();
    if (!v || prefs.includes(v)) return;
    const next = [...prefs, v];
    setPrefs(next);
    saveStylePrefs(next);
    setNewPref("");
  };

  const removePref = (s) => {
    const next = prefs.filter((p) => p !== s);
    setPrefs(next);
    saveStylePrefs(next);
  };

  const confirmCancel = async () => {
    setCancelState("sending");
    try {
      const res = await axios.post(`${API}/subscription/cancel-request`, {
        email: customer.email,
      });
      if (!res.data?.ok) throw new Error("unexpected response");
      setCancelState("done");
    } catch (e) {
      setCancelOpen(false);
      setCancelState("idle");
      toast.error(e?.response?.data?.detail || "Could not send the cancellation request. Try again.");
    }
  };

  const signOut = () => {
    localStorage.removeItem("grapho_customer_email");
    navigate("/");
  };

  return (
    <div data-testid="settings-page">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">Grapho Studio</p>
      <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Settings</h1>

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`settings-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setTab(t)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              tab === t
                ? "border-amber-400/50 bg-amber-400/10 text-amber-300"
                : "border-white/10 bg-white/5 text-slate-400 hover:text-slate-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="glass mt-6 rounded-2xl p-6">
        {tab === "Profile" && (
          <div data-testid="settings-profile">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Account</p>
            <p className="mt-2 truncate text-sm text-slate-200">{customer.email}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Etsy shop</p>
            <p className="mt-2 text-sm text-slate-200">{customer.shop || "Not set"}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Plan</p>
            <p className="mt-2 text-sm text-slate-200">{customer.plan_name}</p>
          </div>
        )}

        {tab === "Creative Preferences" && (
          <div data-testid="settings-preferences">
            <p className="text-sm text-slate-400">
              Styles you use most. They prefill the style field on your New Brief.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {prefs.map((s) => (
                <button
                  key={s}
                  data-testid={`pref-pill-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => removePref(s)}
                  className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-[11px] font-semibold text-amber-300 transition-colors hover:border-rose-400/40 hover:text-rose-300"
                  title="Click to remove"
                >
                  {s} ×
                </button>
              ))}
            </div>
            <form onSubmit={addPref} className="mt-4 flex gap-2">
              <input
                data-testid="pref-add-input"
                type="text"
                value={newPref}
                onChange={(e) => setNewPref(e.target.value)}
                placeholder="Add a style, e.g. Bold Retro"
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-sky-400/60 focus:outline-none"
              />
              <button
                data-testid="pref-add-button"
                type="submit"
                className="rounded-xl bg-amber-400 px-4 py-2.5 text-xs font-bold text-[#1A1204] transition-colors hover:bg-amber-300"
              >
                Add
              </button>
            </form>
          </div>
        )}

        {tab === "Notifications" && (
          <div data-testid="settings-notifications">
            <p className="text-sm text-slate-400">Emails Grapho sends you automatically:</p>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Brief received — confirmation when a request enters the queue</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Designs ready — the moment delivery happens</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Payment receipts — on every successful activation</li>
            </ul>
          </div>
        )}

        {tab === "Subscription" && (
          <div data-testid="settings-subscription">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Your plan</p>
            <p className="mt-2 text-sm text-slate-200">
              {customer.plan_name} · renews {renewalDate}
            </p>
            {customer.cancellation_requested_at ? (
              <span
                data-testid="account-settings-cancelled-chip"
                className="mt-4 inline-block rounded-full border border-rose-400/40 bg-rose-400/10 px-4 py-2 text-xs font-bold text-rose-300"
              >
                Cancellation requested — active until cycle end
              </span>
            ) : (
              <button
                data-testid="cancel-subscription-button"
                onClick={() => {
                  setCancelState("idle");
                  setCancelOpen(true);
                }}
                className="mt-4 rounded-full border border-rose-400/40 bg-rose-400/10 px-5 py-2.5 text-sm font-bold text-rose-300 transition-colors hover:bg-rose-400/20"
              >
                Cancel Subscription
              </button>
            )}
            <p className="mt-3 text-xs text-slate-500">
              Credits stay usable until the end of the current cycle ({renewalDate}).
            </p>
          </div>
        )}

        {tab === "Security" && (
          <div data-testid="settings-security">
            <p className="text-sm text-slate-400">
              You're signed in as {customer.email}. Sessions stay on this device until you sign out.
            </p>
            <button
              data-testid="sign-out-button"
              onClick={signOut}
              className="mt-4 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-bold text-slate-200 transition-colors hover:border-white/30"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>

      {cancelOpen && (
        <div
          data-testid="cancel-confirm-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
        >
          <div className="glass w-full max-w-md rounded-3xl border border-rose-400/30 bg-[#0C1222] p-7">
            {cancelState === "done" ? (
              <div data-testid="cancel-request-success" className="text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                <p className="mt-4 text-lg font-bold text-white">Cancellation request sent</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  Our team will process it before your next billing date. Your {customer.plan_name} plan and
                  remaining credits stay active until the end of this cycle.
                </p>
                <button
                  data-testid="cancel-success-close"
                  onClick={async () => {
                    setCancelOpen(false);
                    await refresh();
                  }}
                  className="mt-6 w-full rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-[#1A1204] transition-colors hover:bg-amber-300"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rose-400">Confirm cancellation</p>
                <p className="mt-3 text-lg font-bold text-white">Cancel your subscription?</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  Your plan remains active until the end of your current billing period ({renewalDate}). No
                  partial refunds are issued for unused days. Your remaining credits stay usable until then.
                </p>
                <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                  <button
                    data-testid="cancel-confirm-decline"
                    onClick={() => setCancelOpen(false)}
                    className="flex-1 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-bold text-slate-200 transition-colors hover:border-white/30"
                  >
                    Keep my plan
                  </button>
                  <button
                    data-testid="cancel-confirm-accept"
                    disabled={cancelState === "sending"}
                    onClick={confirmCancel}
                    className="flex-1 rounded-full border border-rose-400/50 bg-rose-500/20 px-5 py-3 text-sm font-bold text-rose-200 transition-colors hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {cancelState === "sending" ? "Sending..." : "Yes, request cancellation"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
